// CreditCardCollectionPage.tsx
import React, {FC, useState, useEffect} from 'react'
import {Form, Formik} from 'formik'
import {
  ICreditCardCollectionModel,
  initialCreditCardCollectionValues,
  creditCardCollectionSchema,
  CARD_TYPE,
  TRANSACTION_TYPE,
  AUTHENTICATION_TYPE,
  FINANCIAL_INFOTYPE,
  CURRENCY_TYPE,
} from './CreditCardCollectionModel'
import {createCreditCardCollection} from './Api'
import {ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import FormTabHeader from '../../../../shared/components/FormTabHeader'
import TextInput from '../../../../shared/components/TextInput'
import SelectInput from '../../../../shared/components/SelectInput'
import TextAreaInput from '../../../../shared/components/TextAreaInput'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import AsyncSelectInput from '../../../../shared/components/AsyncSelectInput'
import {branches, fetchAsyncCustomerData} from '../../../../shared/models/SharedApiLookups'
import AsyncSearchInput from '../../../../shared/components/AsyncSearchInput'
import { AsyncCustomerModel } from '../../../../shared/models/ApiModels'

const tabOptions = [
  {
    key: 'form',
    name: 'Kredi Kartı Tahsilat İşlemi',
    title: 'Kredi Kartı Tahsilat İşlemi',
  },
]

export const CreditCardCollectionPage: FC = () => {
  const [tab, setTab] = useState<string>('form')
  const [loading, setLoading] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(0)
  const [options, setOptions] = useState<{value: string; label: string}[]>([])

  const handleSave = async (values: ICreditCardCollectionModel, resetForm: () => void) => {
    setLoading(true)
    try {
      if (selectedId) {
        // update işlemi eklenebilir
      } else {
        // Tarihi backend uyumlu formata çevir
        values.transactionDate = new Date(values.transactionDate).toISOString().split('T')[0]

        await createCreditCardCollection(values)
        showSuccessToast('Kredi Kartı ve Havale İşlemi başarıyla oluşturuldu')
      }
      setCurrentPage(0)
      resetForm()
      setSelectedId(null)
      setTab('form')
    } catch (err) {
      showErrorToast('Bir hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  // Şube arama fonksiyonu
  useEffect(() => {
    async function loadBranches() {
      try {
        const data = await branches()
        setOptions(data)
      } catch (err) {
        console.error('Şubeler yüklenemedi', err)
      }
    }
    loadBranches()
  }, [])

  return (
    <>
      <Formik
        initialValues={initialCreditCardCollectionValues}
        validationSchema={creditCardCollectionSchema}
        enableReinitialize
        onSubmit={(values, {resetForm}) => handleSave(values, resetForm)}
      >
        {(formik) => {
          const handleReset = () => {
            formik.resetForm()
            setSelectedId(null)
          }

          return (
            <>
              <FormTabHeader
                formId='creditCardCollectionForm'
                formikSubmit={formik.handleSubmit}
                formikReset={handleReset}
                loading={loading}
                tab={tab}
                setTab={setTab}
                setSelectedId={setSelectedId}
                tabs={tabOptions}
                onExportExcel={() => console.log('Excel export')}
              />

              {tab === 'form' && (
                <div className='card mb-5'>
                  <Form id='creditCardCollectionForm' className='form card-body'>
                    {/* Cari seçimi */}
                    <div className='row mb-6'>
                      <AsyncSearchInput<AsyncCustomerModel>
                        name="customerId"
                        label='Cari Ara'
                        placeholder='Ad Soyad girin...'
                        fetchHandler={fetchAsyncCustomerData}
                        displayField={(item) => `${item.firstName} ${item.lastName}`}
                        onSelect={(item) => console.log('Seçilen cari:', item)}
                        onClear={() => console.log('Cari temizlendi')}
                      />
                    </div>

                    {/* Cari bilgiler */}
                    <div className='row mb-6'>
                      <TextInput name='firstName' label='Ad' wrapperClassName='col-md-6' />
                      <TextInput name='lastName' label='Soyad' wrapperClassName='col-md-6' />
                    </div>

                    {/* İşlem bilgileri */}
                    <div className='row mb-6'>
                      <TextInput
                        name='transactionDate'
                        type='date'
                        label='İşlem Tarihi'
                        wrapperClassName='col-md-6'
                      />
                      <SelectInput
                        name='currency'
                        label='Para Birimi'
                        wrapperClassName='col-md-6'
                        options={CURRENCY_TYPE}
                      />
                    </div>

                    {/* Tutarlar */}
                    <div className='row mb-6'>
                      <TextInput
                        name='bankAmount'
                        type='number'
                        label='Banka Tutarı'
                        wrapperClassName='col-md-6'
                      />
                      <TextInput
                        name='customerAmount'
                        type='number'
                        label='Müşteri Tutarı'
                        wrapperClassName='col-md-6'
                      />
                    </div>

                    {/* Kurlar */}
                    <div className='row mb-6'>
                      <TextInput
                        name='bankRate'
                        type='number'
                        label='Banka Kuru'
                        wrapperClassName='col-md-6'
                      />
                      <TextInput
                        name='customerRate'
                        type='number'
                        label='Müşteri Kuru'
                        wrapperClassName='col-md-6'
                      />
                    </div>

                    {/* Makbuz ve IBAN */}
                    <div className='row mb-6'>
                      <TextInput
                        name='receiptNumber'
                        label='Makbuz No'
                        wrapperClassName='col-md-6'
                      />
                      <TextInput name='iban' label='IBAN' wrapperClassName='col-md-6' />
                    </div>

                    {/* Şube ve Operatör */}
                    <div className='row mb-6'>
                      <SelectInput
                        name='branch'
                        label='Şube'
                        wrapperClassName='col-md-6'
                        options={options}
                      />
                      <TextInput name='operator' label='İşlem Yapan' wrapperClassName='col-md-6' />
                    </div>

                    {/* Kart bilgileri */}
                    <div className='row mb-6'>
                      <TextInput
                        name='cardNumber'
                        label='Kart Numarası'
                        wrapperClassName='col-md-6'
                      />
                      <TextInput name='cvv' label='CVV' wrapperClassName='col-md-6' />
                    </div>

                    <div className='row mb-6'>
                      <TextInput
                        name='cardHolder'
                        label='Kart Sahibi'
                        wrapperClassName='col-md-6'
                      />
                      <SelectInput
                        name='cardType'
                        label='Kart Tipi'
                        options={CARD_TYPE}
                        wrapperClassName='col-md-6'
                      />
                    </div>

                    {/* Finans bilgileri */}
                    <div className='row mb-6'>
                      <TextInput
                        name='installmentCount'
                        type='number'
                        label='Taksit Sayısı'
                        wrapperClassName='col-md-6'
                      />
                      <TextInput
                        name='virtualPosNumber'
                        label='Sanal POS No'
                        wrapperClassName='col-md-6'
                      />
                    </div>

                    <div className='row mb-6'>
                      <TextInput name='specialCode' label='Özel Kod' wrapperClassName='col-md-6' />
                      <SelectInput
                        name='financialInfo'
                        label='Finans Bilgisi'
                        wrapperClassName='col-md-6'
                        options={FINANCIAL_INFOTYPE}
                      />
                    </div>

                    {/* İşlem ve doğrulama tipi */}
                    <div className='row mb-6'>
                      <SelectInput
                        name='transactionType'
                        label='İşlem Tipi'
                        options={TRANSACTION_TYPE}
                        wrapperClassName='col-md-6'
                      />
                      <SelectInput
                        name='authenticationType'
                        label='Doğrulama Tipi'
                        options={AUTHENTICATION_TYPE}
                        wrapperClassName='col-md-6'
                      />
                    </div>

                    {/* Açıklama */}
                    <div className='row mb-6'>
                      <TextAreaInput
                        name='description'
                        label='Açıklama'
                        wrapperClassName='col-md-6'
                      />
                    </div>
                  </Form>
                </div>
              )}
            </>
          )
        }}
      </Formik>

      <ToastContainer position='top-right' autoClose={5000} />
    </>
  )
}
